import {useQuery, QueryClient} from '@tanstack/react-query';
import {useDispatch, useSelector} from 'react-redux';
import {Dispatch} from 'redux';
import {LOGIN_SUCCESS} from '../actions/auth';
import {fetchRest} from '../helpers/fetchRest';
import utils from '../helpers/utils';
import {RootState} from '../reducers/rootReducer';

export const QUERY_KEY_MEMBER_DATA = 'memberData';

type LoginSuccessAction = {
  type: typeof LOGIN_SUCCESS;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  user: any;
};

// TODO(ITPS-13568): React Query is only a flag layer here — no consumer reads
// `data` from useMemberData; member data actually lives in Redux store.auth.user
// (~130 readers). The long-term fix (per CLAUDE.md's Redux→RQ migration) is to make
// React Query the source of truth and drop the Redux dual-write below. Tracked
// separately; out of scope for this change.

/**
 * Builds the queryFn for the /v1/members endpoint, shared by useMemberData and
 * refetchMemberData so the fetch + Redux-sync logic lives in exactly one place.
 *
 * The LOGIN_SUCCESS dispatch keeps Redux store.auth.user in sync — it remains the
 * app's source of truth for member data, so every read of auth.user re-renders
 * with the latest values.
 */
const createMemberQueryFn = (dispatch: Dispatch<LoginSuccessAction>, url: string) => async() => {
  const data = await fetchRest(url, {
    method: 'GET',
    headers: {Accept: 'application/json'},
    cache: 'no-store',
  });
  dispatch({type: LOGIN_SUCCESS, user: data});
  return data;
};

/**
 * Force-refetch member data (e.g., after PII updates), even though useMemberData
 * registers the query as disabled.
 *
 * Routes through React Query's own fetchQuery, which writes the result to the cache
 * and flips the query status to 'success' — no manual setQueryData needed.
 *
 * @param queryClient - React Query client instance
 * @param dispatch - Redux dispatch function
 * @returns Promise resolving to the fetched member data
 * @throws Error if fetch fails
 */
export const refetchMemberData = async(queryClient: QueryClient, dispatch: Dispatch<LoginSuccessAction>) => {
  const url = utils.getMembersPath();

  try {
    // staleTime: 0 is REQUIRED. useMemberData registers this queryKey with
    // staleTime: Infinity, so without an override fetchQuery treats the cached value
    // as fresh and returns it WITHOUT a network call. Passing 0 forces the round-trip.
    return await queryClient.fetchQuery({
      queryKey: [QUERY_KEY_MEMBER_DATA],
      queryFn: createMemberQueryFn(dispatch, url),
      staleTime: 0,
    });
  } catch (error) {
    console.error('Failed to refetch member data:', error);
    throw error; // Re-throw for caller to handle
  }
};

/**
 * React Query wrapper around the /v1/members endpoint.
 *
 * This hook does NOT fetch automatically (enabled: false) - it only reads from cache.
 * The cache is populated by:
 * 1. App.tsx calling loginRefresh() which fetches /v1/members and updates Redux
 * 2. App.tsx calling queryClient.setQueryData() to seed the React Query cache
 *
 * Before the cache is seeded, components get data via placeholderData from Redux auth.user.
 * Once cache is seeded, components receive the cached data.
 *
 * To force a re-fetch (e.g., after PII updates):
 *   import {refetchMemberData} from './useMemberData'
 *   await refetchMemberData(queryClient, dispatch)
 *
 * @return {Object} React Query result with isLoading mapped from isPending
 */
export const useMemberData = () => {
  const dispatch = useDispatch();
  const memberUser = useSelector((store: RootState) => store.auth.user);
  const url = utils.getMembersPath();

  const query = useQuery({
    queryKey: [QUERY_KEY_MEMBER_DATA],
    queryFn: createMemberQueryFn(dispatch, url),
    placeholderData: memberUser,
    // Never fetch automatically - only rely on cache seeded by App.tsx loginRefresh
    // For explicit refetches (e.g., after PII updates), use refetchMemberData() helper
    enabled: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  return {...query, isLoading: query.isPending};
};
